import React, { useEffect, useState } from 'react';
import { API_BASE } from '../../config/api';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const loadRazorpayScript = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve(true);
    return;
  }

  const existingScript = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
  if (existingScript) {
    existingScript.addEventListener('load', () => resolve(true), { once: true });
    existingScript.addEventListener('error', () => reject(new Error('Could not load Razorpay checkout')), { once: true });
    return;
  }

  const script = document.createElement('script');
  script.src = RAZORPAY_SCRIPT_URL;
  script.async = true;
  script.onload = () => resolve(true);
  script.onerror = () => reject(new Error('Could not load Razorpay checkout'));
  document.body.appendChild(script);
});

const getResponseErrorMessage = async (response, fallbackMessage) => {
  const errorData = await response.json().catch(() => null);
  return errorData?.message || fallbackMessage;
};

const RazorpayPayment = ({
  totalAmount,
  itemsPrice = 0,
  shippingPrice = 0,
  taxPrice = 0,
  selectedPaymentMethod,
  onCreatePaymentPayload,
  onSuccess,
  onError,
  onBack
}) => {
  const [razorpayLoaded, setRazorpayLoaded] = useState(Boolean(window.Razorpay));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    loadRazorpayScript()
      .then(() => {
        if (isMounted) {
          setRazorpayLoaded(true);
        }
      })
      .catch((scriptError) => {
        if (isMounted) {
          setError(scriptError.message || 'Could not initialize payment system.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const failPayment = (paymentState, message) => {
    const finalMessage = message || (
      paymentState === 'incomplete'
        ? 'Payment not completed. Please proceed again.'
        : 'Payment failed. Please try again.'
    );

    setError(finalMessage);
    if (onError) {
      onError({ paymentState, message: finalMessage });
    }
  };

  const verifyPayment = async (paymentResponse) => {
    const response = await fetch(`${API_BASE}/payment/razorpay/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
      }),
    });

    if (!response.ok) {
      throw new Error(await getResponseErrorMessage(response, 'Payment verification failed.'));
    }

    const data = await response.json();
    if (!data.success || !data.order) {
      throw new Error(data.message || 'Payment verification failed.');
    }

    const paymentMethod = {
      id: 'razorpay',
      name: 'Online Payment',
    };

    sessionStorage.setItem('lastOrder', JSON.stringify(data.order));
    sessionStorage.setItem('lastPaymentMethod', JSON.stringify(paymentMethod));

    onSuccess(data.order, paymentMethod);
  };

  const handlePayment = async () => {
    if (!razorpayLoaded || !window.Razorpay) {
      setError('Payment system is still loading. Please try again in a moment.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const paymentPayload = await onCreatePaymentPayload();

      const orderResponse = await fetch(`${API_BASE}/payment/razorpay/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(paymentPayload),
      });

      if (!orderResponse.ok) {
        throw new Error(await getResponseErrorMessage(orderResponse, 'Could not create payment order.'));
      }

      const razorpayOrder = await orderResponse.json();
      if (!razorpayOrder?.order_id || !razorpayOrder?.key) {
        throw new Error('Payment gateway did not return a valid order.');
      }

      const checkout = new window.Razorpay({
        key: razorpayOrder.key,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'Panchali Jewels',
        description: 'Secure online payment',
        order_id: razorpayOrder.order_id,
        prefill: {
          name: razorpayOrder.customer?.name || paymentPayload.shippingAddress?.fullName || '',
          email: razorpayOrder.customer?.email || '',
          contact: razorpayOrder.customer?.contact || paymentPayload.shippingAddress?.phone || '',
        },
        notes: {
          store: 'Panchali Jewels',
        },
        theme: {
          color: '#123257',
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            failPayment('incomplete', 'Payment was not completed. Your cart is still saved.');
          },
        },
        handler: async (paymentResponse) => {
          try {
            await verifyPayment(paymentResponse);
          } catch (verifyError) {
            failPayment('failed', verifyError.message || 'Payment verification failed. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
      });

      checkout.on('payment.failed', (failureResponse) => {
        const failureMessage = failureResponse?.error?.description
          || failureResponse?.error?.reason
          || 'Payment failed. Please try again.';
        setLoading(false);
        failPayment('failed', failureMessage);
      });

      checkout.open();
    } catch (paymentError) {
      setLoading(false);
      failPayment('failed', paymentError.message || 'Payment initialization failed. Please try again.');
    }
  };

  return (
    <div className="razorpay-payment-container payment-stage-panel payment-stage-panel--online">
      <div className="payment-stage-intro">
        <div>
          <span className="payment-stage-badge payment-stage-badge--success">Online Payment</span>
          <h3>Secure Razorpay Checkout</h3>
          <p>
            Continue to Razorpay to pay securely by card, UPI, netbanking, or wallet.
            Your order is created only after backend payment verification succeeds.
          </p>
        </div>

        <div className="razorpay-payment-badges">
          <span className="payment-summary-method-pill">{selectedPaymentMethod?.name || 'Online Payment'}</span>
          <span className="payment-summary-method-pill payment-method-status-pill--success">
            Protected
          </span>
        </div>
      </div>

      {error ? (
        <div className="payment-error">
          <div className="payment-error-content">
            <strong>Payment Error</strong>
            <p>{error}</p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="payment-loading">
          <div className="payment-loader"></div>
          <p>Processing your payment...</p>
        </div>
      ) : (
        <>
          <div className="payment-stage-grid">
            <div className="payment-stage-detail-card payment-stage-detail-card--primary">
              <span className="payment-stage-label">Selected payment flow</span>
              <strong>Razorpay Checkout</strong>
              <p>Use your preferred payment option inside the secure Razorpay window.</p>
              <div className="payment-method-logos">
                <span className="payment-logo visa">Visa</span>
                <span className="payment-logo mastercard">Mastercard</span>
                <span className="payment-logo upi">UPI</span>
                <span className="payment-logo netbanking">Netbanking</span>
              </div>
            </div>

            <div className="payment-stage-detail-card">
              <span className="payment-stage-label">Amount to pay</span>
              <strong className="payment-stage-amount">{formatCurrency(totalAmount)}</strong>
              <div className="payment-stage-mini-list">
                <div className="payment-stage-mini-row">
                  <span>Items</span>
                  <strong>{formatCurrency(itemsPrice || totalAmount)}</strong>
                </div>
                <div className="payment-stage-mini-row">
                  <span>Shipping</span>
                  <strong>FREE</strong>
                </div>
                {taxPrice > 0 ? (
                  <div className="payment-stage-mini-row">
                    <span>Taxes</span>
                    <strong>{formatCurrency(taxPrice)}</strong>
                  </div>
                ) : null}
                <div className="payment-stage-mini-row">
                  <span>Total</span>
                  <strong>{formatCurrency(totalAmount)}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="payment-stage-note">
            <p>The backend recalculates the amount before Razorpay order creation. Payment success is shown only after signature verification.</p>
          </div>

          <div className="payment-stage-actions">
            <button
              type="button"
              className="back-button"
              onClick={onBack}
              disabled={loading}
            >
              Change Method
            </button>
            <button
              type="button"
              className="pay-button"
              onClick={handlePayment}
              disabled={loading || !razorpayLoaded}
            >
              {razorpayLoaded ? `Pay ${formatCurrency(totalAmount)}` : 'Loading Payment'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RazorpayPayment;
